/**
 * Settings column: every simulation parameter, grouped by the process it drives.
 *
 * The groups mirror the pass order in `SimulationEngine`, so the panel reads as a
 * description of the pipeline: Terrain generates, then Hydraulic → Gravity → Wind
 * run each tick, and Simulation controls how often.
 */

import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { LIMITS, TERRAIN_PRESETS, type SimParams, type TerrainPreset } from '../config.ts';
import { RESOLUTIONS } from '../simulation/constants.ts';
import type { BrushMode } from '../simulation/types.ts';
import { Group, NumberInput, Select, SliderRow, Toggle } from './controls.tsx';
import { useRuntimeVersion, type SimulationRuntime } from '../app/runtime.ts';

const BRUSH_MODES: { value: BrushMode; label: string }[] = [
  { value: 'raise', label: '抬升地形' },
  { value: 'lower', label: '下切地形' },
  { value: 'water', label: '注水' },
  { value: 'sediment', label: '添加泥沙' },
  { value: 'hardness', label: '改变硬度' },
  { value: 'smooth', label: '平滑' },
];

function Collapsible({
  title,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  enabled?: boolean;
  onToggle?: (v: boolean) => void;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section className="border-b border-rule last:border-b-0">
      <header className="flex items-center gap-1.5 px-3 py-1.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex flex-1 items-center gap-1 text-left"
        >
          {open ? <ChevronDown size={11} className="text-ink-faint" /> : <ChevronRight size={11} className="text-ink-faint" />}
          <span className="text-[10px] uppercase tracking-[0.14em] text-ink-faint">{title}</span>
        </button>
        {onToggle ? (
          <input
            type="checkbox"
            checked={Boolean(enabled)}
            onChange={(e) => onToggle(e.target.checked)}
            title={`Enable or disable the ${title.toLowerCase()} process`}
          />
        ) : null}
      </header>
      {open ? <div className="px-3 pb-2.5">{children}</div> : null}
    </section>
  );
}

export function SettingsPanel({ runtime }: { runtime: SimulationRuntime }) {
  useRuntimeVersion(runtime);
  const p = runtime.getParams();
  const t = LIMITS.terrain;
  const h = LIMITS.hydraulic;
  const g = LIMITS.gravity;
  const w = LIMITS.wind;
  const e = LIMITS.engine;

  const setTerrain = (fn: (x: SimParams['terrain']) => void) => runtime.mutate((s) => fn(s.terrain));
  const setHydraulic = (fn: (x: SimParams['hydraulic']) => void) => runtime.mutate((s) => fn(s.hydraulic));
  const setGravity = (fn: (x: SimParams['gravity']) => void) => runtime.mutate((s) => fn(s.gravity));
  const setWind = (fn: (x: SimParams['wind']) => void) => runtime.mutate((s) => fn(s.wind));
  const setEngine = (fn: (x: SimParams['engine']) => void) => runtime.mutate((s) => fn(s.engine));

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <Collapsible title="地形">
        <div className="mb-1.5">
          <span className="mb-0.5 block text-[11px] leading-tight text-ink-muted">预设</span>
          <Select
            value={p.terrain.preset}
            options={TERRAIN_PRESETS.map((x) => ({ value: x.id, label: x.label }))}
            onChange={(v) => runtime.setPreset(v as TerrainPreset)}
          />
          <p className="mt-0.5 text-[10px] leading-tight text-ink-faint">
            {TERRAIN_PRESETS.find((x) => x.id === p.terrain.preset)?.hint}
          </p>
        </div>
        <div className="mb-1.5 grid grid-cols-2 gap-2">
          <div>
            <span className="mb-0.5 block text-[11px] leading-tight text-ink-muted">分辨率</span>
            <Select
              value={p.terrain.resolution}
              options={RESOLUTIONS.map((r) => ({ value: r, label: `${r} × ${r}` }))}
              onChange={(v) => runtime.setResolution(Number(v))}
            />
          </div>
          <div>
            <span className="mb-0.5 block text-[11px] leading-tight text-ink-muted">种子</span>
            <NumberInput
              value={p.terrain.seed}
              min={t.seed.min}
              max={t.seed.max}
              onChange={(v) => runtime.setSeed(Math.round(v))}
            />
          </div>
        </div>
        <SliderRow
          label={t.cellSize.label}
          hint={t.cellSize.hint}
          unit={t.cellSize.unit}
          value={p.terrain.cellSize}
          min={t.cellSize.min}
          max={t.cellSize.max}
          step={t.cellSize.step}
          onChange={(v) => setTerrain((x) => void (x.cellSize = v))}
        />
        <p className="mb-1 mt-0.5 text-[10px] leading-tight text-ink-faint">
          世界范围{' '}
          <span className="font-mono">
            {((p.terrain.resolution * p.terrain.cellSize) / 1000).toFixed(1)} km
          </span>{' '}
          见方
        </p>
        <SliderRow
          label={t.scale.label}
          hint={t.scale.hint}
          value={p.terrain.scale}
          min={t.scale.min}
          max={t.scale.max}
          step={t.scale.step}
          onChange={(v) => setTerrain((x) => void (x.scale = v))}
        />
        <SliderRow
          label={t.height.label}
          hint="生成地形的最高高程"
          unit={t.height.unit}
          value={p.terrain.height}
          min={t.height.min}
          max={t.height.max}
          step={t.height.step}
          onChange={(v) => setTerrain((x) => void (x.height = v))}
        />
        <SliderRow
          label={t.roughness.label}
          hint={t.roughness.hint}
          value={p.terrain.roughness}
          min={t.roughness.min}
          max={t.roughness.max}
          step={t.roughness.step}
          onChange={(v) => setTerrain((x) => void (x.roughness = v))}
        />
        <SliderRow
          label={t.sharpness.label}
          hint={t.sharpness.hint}
          value={p.terrain.sharpness}
          min={t.sharpness.min}
          max={t.sharpness.max}
          step={t.sharpness.step}
          onChange={(v) => setTerrain((x) => void (x.sharpness = v))}
        />
        <SliderRow
          label={t.warp.label}
          hint={t.warp.hint}
          value={p.terrain.warp}
          min={t.warp.min}
          max={t.warp.max}
          step={t.warp.step}
          onChange={(v) => setTerrain((x) => void (x.warp = v))}
        />
        <p className="mt-1 text-[10px] leading-tight text-ink-faint">
          地形参数在点击<em>重新生成</em>后才会生效。
        </p>
      </Collapsible>

      <Collapsible
        title="水力"
        enabled={p.hydraulic.enabled}
        onToggle={(v) => setHydraulic((x) => void (x.enabled = v))}
      >
        <SliderRow
          label={h.rainfall.label}
          hint="降雨强度；除笔刷注水外唯一的水源"
          unit={h.rainfall.unit}
          value={p.hydraulic.rainfall}
          min={h.rainfall.min}
          max={h.rainfall.max}
          step={h.rainfall.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.rainfall = v))}
        />
        <SliderRow
          label={h.flowRate.label}
          hint={h.flowRate.hint}
          value={p.hydraulic.flowRate}
          min={h.flowRate.min}
          max={h.flowRate.max}
          step={h.flowRate.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.flowRate = v))}
        />
        <SliderRow
          label={h.flowDamping.label}
          hint="流速诊断中的阻力；越大流动越黏滞"
          value={p.hydraulic.flowDamping}
          min={h.flowDamping.min}
          max={h.flowDamping.max}
          step={h.flowDamping.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.flowDamping = v))}
        />
        <SliderRow
          label={h.erosionStrength.label}
          hint="河床可蚀性，会除以当地硬度"
          value={p.hydraulic.erosionStrength}
          min={h.erosionStrength.min}
          max={h.erosionStrength.max}
          step={h.erosionStrength.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.erosionStrength = v))}
        />
        <SliderRow
          label={h.sedimentCapacity.label}
          hint="水越深、流越快、坡越陡，能携带的泥沙越多"
          value={p.hydraulic.sedimentCapacity}
          min={h.sedimentCapacity.min}
          max={h.sedimentCapacity.max}
          step={h.sedimentCapacity.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.sedimentCapacity = v))}
        />
        <SliderRow
          label={h.depositionRate.label}
          hint="每 tick 沉降掉的超载泥沙比例"
          value={p.hydraulic.depositionRate}
          min={h.depositionRate.min}
          max={h.depositionRate.max}
          step={h.depositionRate.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.depositionRate = v))}
        />
        <SliderRow
          label={h.evaporation.label}
          hint="每小时蒸发损失的水量"
          unit={h.evaporation.unit}
          value={p.hydraulic.evaporation}
          min={h.evaporation.min}
          max={h.evaporation.max}
          step={h.evaporation.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.evaporation = v))}
        />
        <SliderRow
          label={h.infiltration.label}
          hint={h.infiltration.hint}
          unit={h.infiltration.unit}
          value={p.hydraulic.infiltration}
          min={h.infiltration.min}
          max={h.infiltration.max}
          step={h.infiltration.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.infiltration = v))}
        />
        <SliderRow
          label={h.baseflow.label}
          hint={h.baseflow.hint}
          unit={h.baseflow.unit}
          value={p.hydraulic.baseflow}
          min={h.baseflow.min}
          max={h.baseflow.max}
          step={h.baseflow.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.baseflow = v))}
        />
        <SliderRow
          label={h.maxErosionPerTick.label}
          hint="每 tick 高程变化的硬上限"
          unit={h.maxErosionPerTick.unit}
          value={p.hydraulic.maxErosionPerTick}
          min={h.maxErosionPerTick.min}
          max={h.maxErosionPerTick.max}
          step={h.maxErosionPerTick.step}
          disabled={!p.hydraulic.enabled}
          onChange={(v) => setHydraulic((x) => void (x.maxErosionPerTick = v))}
        />
      </Collapsible>

      <Collapsible
        title="重力"
        enabled={p.gravity.enabled}
        onToggle={(v) => setGravity((x) => void (x.enabled = v))}
      >
        <SliderRow
          label={g.talusAngle.label}
          hint="自然休止角；超过这个坡度的坡面会坍塌"
          unit={g.talusAngle.unit}
          value={p.gravity.talusAngle}
          min={g.talusAngle.min}
          max={g.talusAngle.max}
          step={g.talusAngle.step}
          disabled={!p.gravity.enabled}
          onChange={(v) => setGravity((x) => void (x.talusAngle = v))}
        />
        <SliderRow
          label={g.collapseRate.label}
          hint="每次迭代搬运的超额坡面物质比例"
          value={p.gravity.collapseRate}
          min={g.collapseRate.min}
          max={g.collapseRate.max}
          step={g.collapseRate.step}
          disabled={!p.gravity.enabled}
          onChange={(v) => setGravity((x) => void (x.collapseRate = v))}
        />
        <SliderRow
          label={g.iterations.label}
          hint="每 tick 的松弛扫描次数"
          value={p.gravity.iterations}
          min={g.iterations.min}
          max={g.iterations.max}
          step={g.iterations.step}
          disabled={!p.gravity.enabled}
          onChange={(v) => setGravity((x) => void (x.iterations = Math.round(v)))}
        />
      </Collapsible>

      <Collapsible title="风蚀" enabled={p.wind.enabled} onToggle={(v) => setWind((x) => void (x.enabled = v))}>
        <SliderRow
          label={w.direction.label}
          hint="风的去向，自 +x 轴逆时针度量"
          unit={w.direction.unit}
          value={p.wind.direction}
          min={w.direction.min}
          max={w.direction.max}
          step={w.direction.step}
          disabled={!p.wind.enabled}
          onChange={(v) => setWind((x) => void (x.direction = v))}
        />
        <SliderRow
          label={w.speed.label}
          hint="低于阈值风速时沙粒不动；超过后起沙量随风速超出部分增长"
          unit={w.speed.unit}
          value={p.wind.speed}
          min={w.speed.min}
          max={w.speed.max}
          step={w.speed.step}
          disabled={!p.wind.enabled}
          onChange={(v) => setWind((x) => void (x.speed = v))}
        />
        <SliderRow
          label={w.erosion.label}
          hint="松散物质的起沙系数"
          value={p.wind.erosion}
          min={w.erosion.min}
          max={w.erosion.max}
          step={w.erosion.step}
          disabled={!p.wind.enabled}
          onChange={(v) => setWind((x) => void (x.erosion = v))}
        />
        <SliderRow
          label={w.transportDistance.label}
          hint="沙粒一次跃移能跳多远"
          unit={w.transportDistance.unit}
          value={p.wind.transportDistance}
          min={w.transportDistance.min}
          max={w.transportDistance.max}
          step={w.transportDistance.step}
          disabled={!p.wind.enabled}
          onChange={(v) => setWind((x) => void (x.transportDistance = Math.round(v)))}
        />
        <SliderRow
          label={w.deposition.label}
          hint="风影区内沉降下来的载荷比例"
          value={p.wind.deposition}
          min={w.deposition.min}
          max={w.deposition.max}
          step={w.deposition.step}
          disabled={!p.wind.enabled}
          onChange={(v) => setWind((x) => void (x.deposition = v))}
        />
      </Collapsible>

      <Collapsible title="模拟">
        <SliderRow
          label={e.iterationsPerFrame.label}
          hint="每渲染帧最多推进的 tick 数；视口始终按帧率渲染"
          value={p.engine.iterationsPerFrame}
          min={e.iterationsPerFrame.min}
          max={e.iterationsPerFrame.max}
          step={e.iterationsPerFrame.step}
          onChange={(v) => setEngine((x) => void (x.iterationsPerFrame = Math.round(v)))}
        />
        <SliderRow
          label={e.dtSim.label}
          hint="一个 tick 推进多少模拟秒"
          unit={e.dtSim.unit}
          value={p.engine.dtSim}
          min={e.dtSim.min}
          max={e.dtSim.max}
          step={e.dtSim.step}
          onChange={(v) => setEngine((x) => void (x.dtSim = v))}
        />
      </Collapsible>

      <Group title="笔刷">
        <div className="mb-1.5">
          <span className="mb-0.5 block text-[11px] leading-tight text-ink-muted">模式</span>
          <Select
            value={runtime.brush.mode}
            options={BRUSH_MODES}
            onChange={(v) => {
              runtime.brush.mode = v as BrushMode;
              runtime.touch();
            }}
          />
        </div>
        <SliderRow
          label="半径"
          hint="笔刷半径，取世界宽度的比例"
          value={runtime.brush.radius}
          min={0.005}
          max={0.25}
          step={0.005}
          onChange={(v) => {
            runtime.brush.radius = v;
            runtime.touch();
          }}
        />
        <SliderRow
          label="强度"
          hint="每次落笔在笔刷中心施加的米数"
          unit="米"
          value={runtime.brush.strength}
          min={0.5}
          max={80}
          step={0.5}
          onChange={(v) => {
            runtime.brush.strength = v;
            runtime.touch();
          }}
        />
        <p className="mt-1 text-[10px] leading-tight text-ink-faint">
          先在工具栏启用笔刷，然后在地形上拖动。改动会直接写入模拟状态，
          所以下一个 tick 立刻就能看到效果。
        </p>
      </Group>

      <Group title="显示">
        <Toggle
          label="水体"
          hint="除水深模式外，在地形上叠加半透明水层"
          checked={runtime.showWater}
          onChange={(v) => {
            runtime.showWater = v;
            runtime.touch();
          }}
        />
        <Toggle
          label="网格叠加"
          hint="用于参照尺度的网格；循环 16 → 64 → 关"
          checked={runtime.gridStep > 0}
          onChange={(v) => {
            runtime.gridStep = v ? 16 : 0;
            runtime.touch();
          }}
        />
        <SliderRow
          label="亮度"
          value={runtime.exposure}
          min={0.4}
          max={2}
          step={0.02}
          onChange={(v) => {
            runtime.exposure = v;
            runtime.touch();
          }}
        />
      </Group>
    </div>
  );
}
